import { create } from 'zustand'
import { wsUrl } from '@/lib/api-url'

type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'auth_failed'

/** Discriminated union of all WebSocket event types */
export type WsEvent =
  | { type: 'note_changed'; note_id: number; [key: string]: unknown }
  | { type: 'note_generated'; note_id: number; is_suggestion?: boolean; [key: string]: unknown }
  | { type: 'patient_changed'; patient_uuid: string; category?: string; [key: string]: unknown }
  | { type: 'appointment_changed'; appointment_id: number; [key: string]: unknown }
  | { type: 'message.new'; conversation_id: number; message_id: number; [key: string]: unknown }
  | { type: 'message.updated'; conversation_id: number; message_id: number; [key: string]: unknown }
  | { type: 'message.deleted'; conversation_id: number; message_id: number; [key: string]: unknown }
  | { type: 'message.intelligence'; conversation_id: number; message_id: number; [key: string]: unknown }
  | { type: 'typing'; conversation_id: number; user_id: number; is_typing: boolean }
  | { type: 'presence'; user_id: number; status: string }
  | { type: 'thread.summary'; conversation_id: number; [key: string]: unknown }
  | { type: 'meeting.patient_waiting'; meeting_code: string; [key: string]: unknown }
  | { type: 'doctor.availability_changed'; [key: string]: unknown }
  | { type: 'treatment_plan.reviewed'; [key: string]: unknown }
  | { type: 'call.matched'; queue_id?: number; call_id?: number; [key: string]: unknown }
  | { type: 'transcript.partial'; note_id: number; text: string }
  | { type: 'transcript.final'; note_id: number; text: string; full_transcript: string }
  | { type: 'transcript.intelligence'; note_id: number; suggestions: unknown }
  | { type: 'transcript.error'; note_id: number; error: string }
  | { type: 'md_inbox.updated'; kind?: string; [key: string]: unknown }
  | { type: 'md_inbox.cleared'; uuid: string; [key: string]: unknown }
  | { type: 'incident.created'; uuid: string; [key: string]: unknown }
  | { type: 'incident.updated'; uuid: string; [key: string]: unknown }
  | { type: 'incident.transitioned'; uuid: string; to_status: string; [key: string]: unknown }
  | { type: 'artifact.created'; uuid: string; [key: string]: unknown }
  | { type: 'artifact.updated'; uuid: string; [key: string]: unknown }
  | { type: 'task_changed'; task_ref: string; id: number; status: string; [key: string]: unknown }
  | { type: string; [key: string]: unknown }  // fallback for unknown events

interface WsState {
  status: WsStatus
  connect: () => void
  disconnect: () => void
}

// Reconnect tuning (CLA-708 follow-up): a fast first retry + jitter so a
// transient connect failure doesn't strand the user on "Connecting" for
// 15-30s (the old behaviour: a flat 5s first wait, then 10s, then 20s, with
// no immediate retry).
const INITIAL_BACKOFF = 1000
// Capped low on purpose: a doctor staring at "Connecting..." for 30s reads
// it as an outage. 10s worst-case between attempts is still gentle on the
// server, and nudgeReconnect() below short-circuits the wait entirely when
// we have a concrete reason to believe a retry will succeed.
const MAX_BACKOFF = 10000
// Ceiling for a connection that is going nowhere (CLA-1382). 10s is the right
// pace for a doctor watching "Connecting..." — it is the wrong pace for a tab
// nobody is looking at, or for a session the server will keep refusing.
//
// ⚠ THE WEB CLIENT CANNOT SEE AN AUTH REJECTION. The server refuses an
// unauthenticated upgrade with HTTP 401 *before* the WebSocket handshake
// completes, and browsers deliberately do not expose the handshake's HTTP
// status to JS — onclose reports a generic 1006. So the WS_AUTH_FAILED (4001)
// path below, which is correct, can never fire for a 401: to the client a
// logged-out session is indistinguishable from an unreachable server.
//
// Measured 2026-09-15: one signed-out tab made 5,303 requests to /ws in 23
// hours, every one a 401, every one retried ~10s later forever.
//
// Until the server closes with 4001 instead (tracked separately), this is
// mitigation, not a fix: back off hard once the evidence says retrying is not
// working, and let visibility/online/session-refresh nudges bring it straight
// back when something actually changes.
const COLD_MAX_BACKOFF = 60000
// How many attempts that never reached OPEN before the cause is treated as
// persistent rather than transient. Five keeps a genuine blip on the fast path
// for someone watching the indicator; a hidden tab has nobody watching, so it
// gives up on the fast path far sooner.
const COLD_AFTER_FAILURES_VISIBLE = 5
const COLD_AFTER_FAILURES_HIDDEN = 2
// How long an outage must persist before we surface "Connecting" — a brief
// reconnect blip stays invisible (the indicator keeps showing "Connected").
const CONNECTING_GRACE = 1500

let socket: WebSocket | null = null
let backoff = INITIAL_BACKOFF
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
// True while the app wants a live socket (connect() called, no disconnect()).
// Guards nudgeReconnect() from resurrecting a deliberately-closed connection.
let wanted = false
// True from the moment a connected socket drops until the next successful
// open — that open is a RECONNECT, and consumers need to catch up on every
// event the outage swallowed (dispatched as a synthetic 'ws.reconnected').
let droppedWhileConnected = false
let connectingTimer: ReturnType<typeof setTimeout> | null = null
// Consecutive attempts that closed without ever reaching OPEN. Reset by a
// successful open, so a socket that connects and later drops stays on the fast
// path — only a connection that never lands is treated as persistently broken.
let failuresWithoutOpen = 0

// A hidden tab is not a doctor waiting on the indicator, so it does not need
// the fast path. Treated as "cold" so a backgrounded tab retries once a minute
// instead of six times — the visibilitychange listener at the bottom of this
// file nudges an immediate reconnect the moment it is looked at again.
function isHidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

function isCold() {
  const threshold = isHidden() ? COLD_AFTER_FAILURES_HIDDEN : COLD_AFTER_FAILURES_VISIBLE
  return failuresWithoutOpen >= threshold
}

/** Event listeners registered by components */
type Listener = (event: WsEvent) => void
const listeners = new Set<Listener>()

export function onWsEvent(fn: Listener) {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

export function wsSend(data: Record<string, unknown>) {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(data))
  }
}

function dispatch(event: WsEvent) {
  listeners.forEach((fn) => fn(event))
}

/** WebSocket close code used by the server when auth fails */
const WS_AUTH_FAILED = 4001

export const useWsStore = create<WsState>((set) => ({
  // 'connecting' on first load so the indicator reads "Connecting..." until
  // the initial socket opens, not a momentary "Offline".
  status: 'connecting',

  connect: () => {
    wanted = true
    if (socket?.readyState === WebSocket.OPEN) return

    const ws = new WebSocket(wsUrl('/ws'))
    socket = ws
    // Per-attempt, not module-level: onclose needs to know whether THIS socket
    // ever opened, and a later attempt must not inherit an earlier one's answer.
    let openedThisAttempt = false

    // Handshake watchdog. A WebSocket attempt can HANG in CONNECTING with no
    // error — observed with Firefox + HTTP/3 origins (Firefox can't run WS
    // over H3 and its coalescing logic stalls the handshake for tens of
    // seconds instead of falling back). A hung attempt defeats both the
    // backoff (never fires) and nudgeReconnect (skips in-flight sockets), so
    // bound it: abort and let the normal retry path take a fresh run.
    const handshakeTimer = setTimeout(() => {
      if (socket === ws && ws.readyState === WebSocket.CONNECTING) {
        console.warn('[ws] handshake still pending after 8s — aborting attempt and retrying')
        reportHandshakeStall()
        ws.close()
      }
    }, 8000)

    // Don't flip the indicator to "Connecting" here — onclose arms a single
    // grace timer that surfaces it only if the outage outlasts CONNECTING_GRACE.

    ws.onopen = () => {
      openedThisAttempt = true
      clearTimeout(handshakeTimer)
      if (connectingTimer) {
        clearTimeout(connectingTimer)
        connectingTimer = null
      }
      set({ status: 'connected' })
      backoff = INITIAL_BACKOFF
      // Reaching OPEN is the only evidence that the connection can work, so it
      // is the only thing that clears the cold-start count.
      failuresWithoutOpen = 0
      if (droppedWhileConnected) {
        droppedWhileConnected = false
        // Everything broadcast during the outage is gone — tell views to
        // refetch rather than trust their last WS-driven state.
        dispatch({ type: 'ws.reconnected' })
      }
    }

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data)
        // Only dispatch messages with a valid string type field
        if (msg && typeof msg === 'object' && typeof msg.type === 'string') {
          dispatch(msg)
        }
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = (e) => {
      clearTimeout(handshakeTimer)
      // Count a close that never reached OPEN. A 401 upgrade rejection lands
      // here indistinguishable from "server unreachable" (see COLD_MAX_BACKOFF),
      // which is exactly why the count, not the close code, drives the ceiling.
      if (!openedThisAttempt) failuresWithoutOpen++
      socket = null
      droppedWhileConnected = true

      if (e.code === WS_AUTH_FAILED) {
        // Auth failure — don't reconnect, surface the state
        if (connectingTimer) {
          clearTimeout(connectingTimer)
          connectingTimer = null
        }
        if (reconnectTimer) clearTimeout(reconnectTimer)
        set({ status: 'auth_failed' })
        return
      }

      // Keep the last visible status through a brief reconnect window. A single
      // grace timer, armed on the first drop from 'connected', surfaces
      // "Connecting" only if we're still down after CONNECTING_GRACE; onopen
      // clears it. So a sub-second blip never flashes the indicator, while a
      // genuine outage still reads "Connecting".
      if (!connectingTimer && useWsStore.getState().status === 'connected') {
        connectingTimer = setTimeout(() => set({ status: 'connecting' }), CONNECTING_GRACE)
      }

      if (reconnectTimer) clearTimeout(reconnectTimer)
      const cold = isCold()
      // Once it is judged cold, JUMP to the slow ceiling rather than continuing
      // to double gently towards it. Doubling from 10s would spend another
      // 10+20+40s of retries reaching a conclusion already drawn — and those
      // are exactly the requests this change exists to stop.
      if (cold && backoff < COLD_MAX_BACKOFF) backoff = COLD_MAX_BACKOFF
      const ceiling = cold ? COLD_MAX_BACKOFF : MAX_BACKOFF
      const capped = Math.min(backoff, ceiling)
      const delay = capped + Math.floor(Math.random() * (capped / 2))
      reconnectTimer = setTimeout(() => {
        useWsStore.getState().connect()
      }, delay)
      backoff = Math.min(capped * 2, ceiling)
    }

    ws.onerror = () => {
      ws.close()
    }
  },

  disconnect: () => {
    wanted = false
    if (reconnectTimer) clearTimeout(reconnectTimer)
    if (connectingTimer) {
      clearTimeout(connectingTimer)
      connectingTimer = null
    }
    backoff = INITIAL_BACKOFF
    socket?.close()
    socket = null
    set({ status: 'disconnected' })
  },
}))

let stallsReported = 0

// One diagnostic per session (first two stalls) — enough to see the pattern
// from affected browsers (observed: Firefox taking 30-75s to connect where
// Safari/Chrome are instant; not reproducible in a clean profile).
function reportHandshakeStall() {
  if (stallsReported >= 2) return
  stallsReported++
  import('@sentry/react')
    .then((Sentry) =>
      Sentry.captureMessage('ws handshake stalled >8s', {
        level: 'warning',
        tags: { feature: 'websocket' },
        extra: { userAgent: navigator.userAgent, stallNumber: stallsReported },
      }),
    )
    .catch(() => {})
}

/**
 * Skip the reconnect backoff when something concrete changed that makes a
 * retry likely to succeed — a session refresh just minted fresh cookies, or
 * the browser regained network. No-ops when a socket is already open or
 * in-flight, or when the app deliberately disconnected (logout/unmount).
 */
export function nudgeReconnect() {
  if (!wanted) return
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
  ) {
    return
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  backoff = INITIAL_BACKOFF
  useWsStore.getState().connect()
}

// Regained network is the canonical "retry now" signal.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => nudgeReconnect())
}

// Coming back to the tab is the other one. This is what makes the slower hidden
// and cold ceilings safe to have: a backgrounded or long-failing tab retries
// once a minute, but the instant someone looks at it the wait is abandoned and
// a fresh attempt runs immediately — so the person never waits out a backoff
// they cannot see.
//
// Note nudgeReconnect deliberately does NOT clear failuresWithoutOpen: a nudge
// buys one immediate attempt, and if that also fails the connection drops
// straight back to the cold ceiling rather than earning five more fast retries
// every time the tab is focused.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') nudgeReconnect()
  })
}

